import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { plainToInstance } from 'class-transformer';
import { AxiosError } from 'axios';

import { endpoints } from '../constants/endpoints';
import { AxiosContext, CSRF_ACCESS_COOKIE } from '../contextManager/axios.context';
import { useToast } from '../contextManager/toast.context';
import { useUser } from '../contextManager/user.context';
import { AvailabilityObject, QualificationGroup, UserDetails } from '../types/schemas/abc';
import { errorMessage, getCookie } from '../utils';
import { useQueryClient } from '@tanstack/react-query';

export type Credentials = {
  email: string;
  password: string;
};

export interface registrationData {
  name: string;
  email: string;
  password?: string;
  role: 'tutor' | 'parent';
}

export interface ProfilePatch {
  subjects?: string[];
  levels?: string[];
  description?: string;
  profilePhotoUrl?: string;
  profilePhoto?: File;
  expertise?: Record<string, string[]>;

  university?: 'Oxford' | 'Cambridge';
  college?: string;
  degreeCourse?: string;
  gradYear?: number;
  proofFileUrl?: string;
  proofFile?: File;
  CVFileUrl?: string;
  CVFile?: File;
  hourlyRate?: number;
  offersFreeIntroCall?: boolean;
  offersFreeFirstLesson?: boolean;

  classification?: string;
  qualificationGroups?: QualificationGroup[];
  accountHolderName?: string;
  sortCode?: string;
  accountNumber?: string;

  availability?: AvailabilityObject;
  timezone?: string;
  profileColor?: string;
}

export default function useAuth() {
  const { addToast, updateToast } = useToast();
  const { userDetails, storeUserDetails, clearUserDetails } = useUser();
  const [isLoggedIn, setIsLoggedIn] = useState(userDetails !== undefined);
  const navigate = useNavigate();

  const axios = useContext(AxiosContext);
  const queryClient = useQueryClient();

  const loginUser = async (data: Credentials) => {
    const toastId = `login-${Date.now()}`;
    addToast({ id: toastId, variant: 'loading', title: 'Logging you in...' });
    return axios
      .post(endpoints.login, data, { withCredentials: true })
      .then((response) => {
        const { accessToken, user } = response.data;
        storeUserDetails(plainToInstance(UserDetails, user), accessToken);
        localStorage.setItem('accessToken', accessToken);
        updateToast(toastId, { variant: 'success', title: 'Welcome back!' });
      })
      .catch((error: AxiosError) => {
        const msg =
          error.response?.status === 500
            ? 'The server encountered an error while fetching your details.'
            : errorMessage(error) || 'Login failed.';
        updateToast(toastId, { variant: 'error', title: msg });
        throw error;
      });
  };

  /**
   * Register with full profile + files in a SINGLE multipart request.
   * One round trip: creates user, uploads files, saves profile.
   */
  const registerWithProfile = async (
    account: registrationData,
    profile: ProfilePatch,
  ) => {
    const form = new FormData();

    // Account fields (flat form fields)
    form.append('email', account.email);
    form.append('password', account.password || '');
    form.append('name', account.name);
    form.append('role', account.role);

    // Files (named fields that multer picks up)
    if (profile.profilePhoto) {
      form.append('profilePhoto', profile.profilePhoto);
    }
    if (profile.proofFile) {
      form.append('proofFile', profile.proofFile);
    }
    if (profile.CVFile) {
      form.append('CVFile', profile.CVFile);
    }

    // Profile data as JSON string (strip File objects)
    const profileData = { ...profile };
    delete profileData.profilePhoto;
    delete profileData.proofFile;
    delete profileData.CVFile;
    profileData.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    form.append('profile', JSON.stringify(profileData));

    const toastId = `register-${Date.now()}`;
    addToast({ id: toastId, variant: 'loading', title: 'Creating your account...' });

    try {
      const response = await axios.post(endpoints.register, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { accessToken, user } = response.data;
      storeUserDetails(plainToInstance(UserDetails, user), accessToken);
      localStorage.setItem('accessToken', accessToken);
      updateToast(toastId, { variant: 'success', title: 'Account created!' });
    } catch (error: any) {
      const msg =
        error.response?.status === 500
          ? 'The server encountered an error while processing your registration.'
          : errorMessage(error) || 'Registration failed.';
      updateToast(toastId, { variant: 'error', title: msg });
      throw error;
    }
  };

  // Keep old registerUser for backward compat (parent flow)
  const registerUser = async (data: registrationData) => {
    const toastId = `register-${Date.now()}`;
    addToast({ id: toastId, variant: 'loading', title: 'Creating your account...' });
    return axios
      .post(endpoints.register, data, { withCredentials: true })
      .then((response) => {
        const { accessToken, user } = response.data;
        storeUserDetails(plainToInstance(UserDetails, user), accessToken);
        localStorage.setItem('accessToken', accessToken);
        updateToast(toastId, { variant: 'success', title: 'Account created!' });
      })
      .catch((error: AxiosError) => {
        const msg =
          error.response?.status === 500
            ? 'The server encountered an error while processing your registration.'
            : errorMessage(error) || 'Registration failed.';
        updateToast(toastId, { variant: 'error', title: msg });
        throw error;
      });
  };

  useEffect(() => setIsLoggedIn(userDetails !== undefined), [userDetails]);

  const logoutUser = () => {
    axios
      .delete(endpoints.logout, {
        headers: { 'X-CSRF-TOKEN': getCookie(CSRF_ACCESS_COOKIE) },
      })
      .catch((error: AxiosError) => {
        console.log(error);
      })
      .finally(() => {
        navigate('/');
        clearUserDetails();
        queryClient.clear();
      });
  };

  const patchProfile = async (
    patch: ProfilePatch,
    labels?: { loading?: string; success?: string },
  ) => {
    const toastId = `profile-patch-${Date.now()}`;
    addToast({
      id: toastId,
      variant: 'loading',
      title: labels?.loading ?? 'Saving profile...',
    });
    try {
      const hasFile = patch.profilePhoto instanceof File ||
                      patch.proofFile instanceof File ||
                      patch.CVFile instanceof File;

      let payload: FormData | ProfilePatch;
      let headers: Record<string, string> = {
        'X-CSRF-TOKEN': getCookie(CSRF_ACCESS_COOKIE),
      };

      if (hasFile) {
        const form = new FormData();
        if (patch.profilePhoto instanceof File) form.append('profilePhoto', patch.profilePhoto);
        if (patch.proofFile instanceof File) form.append('proofFile', patch.proofFile);
        if (patch.CVFile instanceof File) form.append('CVFile', patch.CVFile);

        const { profilePhoto, proofFile, CVFile, ...rest } = patch;
        if (Object.keys(rest).length > 0) {
          form.append('profile', JSON.stringify(rest));
        }
        payload = form;
        headers['Content-Type'] = 'multipart/form-data';
      } else {
        payload = patch;
      }

      const { data: user } = await axios.patch(endpoints.profile, payload, {
        withCredentials: true,
        headers,
      });

      const token = localStorage.getItem('accessToken') || '';
      storeUserDetails(plainToInstance(UserDetails, user), token);
      updateToast(toastId, {
        variant: 'success',
        title: labels?.success ?? 'Profile saved.',
      });
    } catch (err: any) {
      const msg =
        err.response?.status === 500
          ? 'The server encountered an error while saving your profile.'
          : errorMessage(err) || 'Save failed.';
      updateToast(toastId, { variant: 'error', title: msg });
      throw err;
    }
  };

  return {
    loginUser,
    logoutUser,
    isLoggedIn,
    registerUser,
    registerWithProfile,
    patchProfile,
  };
}